import React, { useState } from 'react'
import shortid from 'shortid'

import Button from '@react/react-spectrum/Button'
import { Table, THead, TBody, TH, TR, TD } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Select from '@react/react-spectrum/Select'

import Add from '@react/react-spectrum/Icon/Add'
import Delete from '@react/react-spectrum/Icon/Delete'
import ArrowUp from '@react/react-spectrum/Icon/ArrowUp'
import ArrowDown from '@react/react-spectrum/Icon/ArrowDown'

const TYPE_OPTIONS = [
  { label: 'Slider', value: 'slider' },
  { label: 'Picker', value: 'picker' },
  { label: 'Text', value: 'string' },
  { label: 'Number', value: 'number' },
]

// `key` is an internal identifier only (React list key + local slider/picker
// state in SimulatorCol.js) — never shown to end users, so it doesn't need to
// be admin-typed. Existing fields loaded from a live project keep whatever
// `key` they already have; new rows get one derived from their generated `id`.
const normalizeRows = (fields) =>
  (fields || []).map((field) => {
    const id = field.id || shortid()
    return { ...field, id, key: field.key || id }
  })

const createRow = () => {
  const id = shortid()
  return { id, key: id, type: 'slider', min: 0, max: 100 }
}

export function SimulatorEditor({ fields, disabled, onChange }) {
  const [rows, setRows] = useState(() => normalizeRows(fields))
  const hasRows = rows.length > 0

  // Rows are saved as-is — a row switched from picker back to slider (or vice
  // versa) may keep a stale `options`/`min`/`max` key, but the Profile Viewer
  // only reads the keys relevant to a field's current `type`, so this is
  // harmless.
  const commit = (newRows) => {
    setRows(newRows)
    onChange(newRows)
  }

  const updateRow = (id, patch) => commit(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))

  const addRow = () => commit([...rows, createRow()])

  const deleteRow = (id) => commit(rows.filter((row) => row.id !== id))

  const moveRow = (id, direction) => {
    const index = rows.findIndex((row) => row.id === id)
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= rows.length) return

    const updated = [...rows]
    const [moved] = updated.splice(index, 1)
    updated.splice(newIndex, 0, moved)
    commit(updated)
  }

  return (
    <div className='SimulatorEditor profile-viewer-section'>
      {hasRows && (
        <Table className='pvs-table'>
          <THead>
            <TH width='160'>Label</TH>
            <TH width='220'>Path</TH>
            <TH width='100'>Type</TH>
            <TH>Options / Range</TH>
            <TH width='130'>Actions</TH>
          </THead>
          <TBody>
            {rows.map((row, index) => (
              <SimulatorRow
                key={row.id}
                row={row}
                disabled={disabled}
                isFirst={index === 0}
                isLast={index === rows.length - 1}
                onChange={(patch) => updateRow(row.id, patch)}
                onMoveUp={() => moveRow(row.id, -1)}
                onMoveDown={() => moveRow(row.id, 1)}
                onDelete={() => deleteRow(row.id)}
              />
            ))}
          </TBody>
        </Table>
      )}

      <Button label='Add Property' variant='primary' onClick={addRow} disabled={disabled} />
    </div>
  )
}

function SimulatorRow({ row, disabled, isFirst, isLast, onChange, onMoveUp, onMoveDown, onDelete }) {
  const type = row.type || 'slider'

  return (
    <TR>
      <TD>
        <Textfield
          value={row.label || ''}
          placeholder='Property Label'
          onChange={(label) => onChange({ label })}
          disabled={disabled}
        />
      </TD>
      <TD>
        <Textfield
          value={row.path || ''}
          placeholder='Property Selector'
          onChange={(path) => onChange({ path })}
          disabled={disabled}
        />
      </TD>
      <TD>
        <Select value={row.type || 'slider'} options={TYPE_OPTIONS} onChange={(type) => onChange({ type })} disabled={disabled} />
      </TD>
      <TD>
        {type === 'picker' ? (
          <OptionsEditor options={row.options || []} onChange={(options) => onChange({ options })} disabled={disabled} />
        ) : type === 'slider' ? (
          <SliderRangeFields
            min={row.min}
            max={row.max}
            onChange={(patch) => onChange(patch)}
            disabled={disabled}
          />
        ) : null}
      </TD>
      <TD>
        <RowActions
          disabled={disabled}
          disableMoveUp={isFirst}
          disableMoveDown={isLast}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDelete={onDelete}
        />
      </TD>
    </TR>
  )
}

function SliderRangeFields({ min, max, disabled, onChange }) {
  return (
    <div className='SimulatorEditor__rangeFields'>
      <Textfield
        type='number'
        value={min ?? ''}
        placeholder='Min'
        onChange={(value) => onChange({ min: value === '' ? undefined : Number(value) })}
        disabled={disabled}
      />
      <Textfield
        type='number'
        value={max ?? ''}
        placeholder='Max'
        onChange={(value) => onChange({ max: value === '' ? undefined : Number(value) })}
        disabled={disabled}
      />
    </div>
  )
}

function RowActions({ disabled, disableMoveUp, disableMoveDown, onMoveUp, onMoveDown, onDelete }) {
  return (
    <div className='pvs-row-actions'>
      <Button variant='tool' aria-label='Move Up' icon={<ArrowUp />} onClick={onMoveUp} disabled={disabled || disableMoveUp} />
      <Button variant='tool' aria-label='Move Down' icon={<ArrowDown />} onClick={onMoveDown} disabled={disabled || disableMoveDown} />
      <Button variant='tool' aria-label='Delete' icon={<Delete />} onClick={onDelete} disabled={disabled} />
    </div>
  )
}

function OptionsEditor({ options, disabled, onChange }) {
  const addOption = () => onChange([...(options || []), { label: '', value: '' }])

  const updateOption = (index, patch) =>
    onChange(options.map((option, i) => (i === index ? { ...option, ...patch } : option)))

  const removeOption = (index) => onChange(options.filter((_, i) => i !== index))

  return (
    <div className='SimulatorEditor__options'>
      {(options || []).map((option, index) => (
        <div className='SimulatorEditor__options__row' key={index}>
          <Textfield
            value={option.label || ''}
            placeholder='Label'
            onChange={(label) => updateOption(index, { label })}
            disabled={disabled}
          />
          <Textfield
            value={option.value || ''}
            placeholder='Value'
            onChange={(value) => updateOption(index, { value })}
            disabled={disabled}
          />
          <Button
            variant='tool'
            aria-label='Delete option'
            icon={<Delete size='S' />}
            onClick={() => removeOption(index)}
            disabled={disabled}
          />
        </div>
      ))}
      <Button quiet variant='action' icon={<Add size='S' />} onClick={addOption} disabled={disabled}>
        Add option
      </Button>
    </div>
  )
}
